import LearningService from "@/services/LearningService";
import { mapState } from "vuex";

/**
 * Mixin for quiz question components.
 *
 * Provides:
 * - Shared props (question, questionIndex, questionCount, etc.)
 * - postingResponse state and postResponseAndAdvance(payload, { onSuccess, onError })
 * - Common computeds: computedSubmitButton, inProgram, noCorrectAnswersMarked
 * - buildBaseResponse() helper for the shared response payload fields
 */
export default {
  props: {
    question: { type: Object, required: true },
    questionIndex: { type: Number, required: true },
    questionCount: { type: Number, required: true },
    learningCardAssociationId: { type: Number, default: null },
    isSurveyQuestion: { type: Boolean, default: false },
    surveyClientId: { type: Number, default: null },
    whistleToken: { type: String, default: null },
    enteredEmail: { type: String, default: null },
    context: { type: String, default: "survey" },
    preview: { type: Boolean, default: false },
  },
  data() {
    return {
      postingResponse: false,
    };
  },
  computed: {
    ...mapState(["userProfile"]),
    computedSubmitButton() {
      return this.isSurveyQuestion ? "Next" : "Submit";
    },
    isRequired() {
      return this.question?.required !== false;
    },
    inProgram() {
      return !(this.context === "survey" || this.context === "learning");
    },
    isLastQuestion() {
      return this.questionIndex === this.questionCount - 1;
    },
    noCorrectAnswersMarked() {
      if (!this.question || !this.question.LearningQuizAnswers) return true;
      return this.question.LearningQuizAnswers.filter((ans) => ans.correctness > 0).length === 0;
    },
  },
  methods: {
    hasValue(value) {
      if (Array.isArray(value)) return value.length > 0;
      if (value === null || value === undefined) return false;
      if (typeof value === "string") return value.trim().length > 0;
      return true;
    },
    skipOptionalUnanswered(value, { onSuccess } = {}) {
      if (!this.isRequired && !this.hasValue(value)) {
        onSuccess?.call(this);
        return true;
      }
      return false;
    },
    isMissingRequiredValue(value) {
      return this.isRequired && !this.hasValue(value);
    },
    normalizeTextValue(value) {
      if (value === null || value === undefined) return "";
      return String(value).trim();
    },
    buildBaseResponse(extra = {}) {
      // Public surveys are submitted with a whistle token issued for the survey's
      // client. A logged-in respondent's own client must not be stored on that response.
      const isPublicSurvey = this.whistleToken != null;
      const response = {
        clientId: isPublicSurvey
          ? this.surveyClientId ?? this.userProfile?.clientId
          : this.userProfile?.clientId ?? this.surveyClientId,
        learningCardId: this.question.learningCardId,
        learningQuizQuestionId: this.question.learningQuizQuestionId,
        learningCardAssociationId: this.learningCardAssociationId,
        userId: this.whistleToken != null ? null : this.$auth.user.sub,
        ...extra,
      };
      if (this.whistleToken != null) {
        response.externalUserId = this.enteredEmail;
      }
      return response;
    },
    async postResponseAndAdvance(quizQuestionResponse, { onSuccess, onError } = {}) {
      if (this.preview) {
        onSuccess?.call(this);
        return;
      }
      this.postingResponse = true;
      try {
        await LearningService.createQuizResponse(quizQuestionResponse, this.whistleToken);
      } catch (err) {
        console.error("Error posting quiz response", err);
        this.$store.dispatch("showError", {
          title: "Error saving response",
          message: "Please try again",
        });
        onError?.call(this);
        this.postingResponse = false;
        return;
      }
      this.postingResponse = false;
      onSuccess?.call(this);
    },
  },
};
